import { getRememberMe } from '@shared/lib/remember-me';

export const AUTH_TOKEN_STORAGE_KEY = 'paygatex_auth_token';
const TOKEN_KEY = AUTH_TOKEN_STORAGE_KEY;
const REFRESH_TOKEN_KEY = 'paygatex_refresh_token';

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
}

interface SetAccessTokenOptions {
  remember?: boolean;
}

const resolveRemember = (remember?: boolean): boolean => {
  if (remember !== undefined) {
    return remember;
  }

  if (localStorage.getItem(TOKEN_KEY)) {
    return true;
  }

  if (sessionStorage.getItem(TOKEN_KEY)) {
    return false;
  }

  return getRememberMe();
};

const getActiveStorage = (remember?: boolean): Storage => {
  return resolveRemember(remember) ? localStorage : sessionStorage;
};

const getInactiveStorage = (remember?: boolean): Storage => {
  return getActiveStorage(remember) === localStorage ? sessionStorage : localStorage;
};

export const authStorage = {
  getTokens(): AuthTokens | null {
    const accessToken = this.getAccessToken();
    if (!accessToken) return null;

    const refreshToken =
      localStorage.getItem(REFRESH_TOKEN_KEY) ??
      sessionStorage.getItem(REFRESH_TOKEN_KEY) ??
      undefined;

    return {
      accessToken,
      refreshToken,
    };
  },

  setTokens(tokens: AuthTokens, options?: SetAccessTokenOptions): void {
    this.setAccessToken(tokens.accessToken, options);

    if (tokens.refreshToken) {
      const storage = getActiveStorage(options?.remember);
      const other = getInactiveStorage(options?.remember);
      storage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      other.removeItem(REFRESH_TOKEN_KEY);
    }
  },

  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY);
  },

  setAccessToken(token: string, options?: SetAccessTokenOptions): void {
    const storage = getActiveStorage(options?.remember);
    const other = getInactiveStorage(options?.remember);
    storage.setItem(TOKEN_KEY, token);
    other.removeItem(TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY) ?? sessionStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setRefreshToken(token: string, options?: SetAccessTokenOptions): void {
    const storage = getActiveStorage(options?.remember);
    const other = getInactiveStorage(options?.remember);
    storage.setItem(REFRESH_TOKEN_KEY, token);
    other.removeItem(REFRESH_TOKEN_KEY);
  },

  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  hasTokens(): boolean {
    return !!this.getAccessToken();
  },
};
